/**
 * Talk Sessions API Client
 * CRUD operations for Talk with Martha conversation sessions.
 * Also merges in agent conversations so they appear in the sidebar.
 */

export interface TalkSessionInfo {
  id: string;
  title: string | null;
  type: string;
  active_project_id: string | null;
  message_count: number;
  created_at: string;
  updated_at: string;
  /** 'talk' for Talk sessions, 'agent' for agent conversations */
  _source?: 'talk' | 'agent';
}

export interface TalkSessionMessage {
  id: number;
  session_id: string;
  role: 'user' | 'assistant';
  content: unknown;
  tokens_estimated: number;
  created_at: string;
}

export const talkSessionsApi = {
  list: async (): Promise<TalkSessionInfo[]> => {
    // Fetch both talk sessions and agent conversations in parallel
    const [talkRes, agentRes] = await Promise.allSettled([
      fetch('/api/ask/sessions', { credentials: 'include' }),
      fetch('/api/orchestration/agent-conversations?limit=20', { credentials: 'include' }),
    ]);

    const talkSessions: TalkSessionInfo[] = [];
    if (talkRes.status === 'fulfilled' && talkRes.value.ok) {
      const data = await talkRes.value.json();
      for (const s of data.sessions ?? []) {
        talkSessions.push({ ...s, _source: 'talk' as const });
      }
    }

    const agentSessions: TalkSessionInfo[] = [];
    if (agentRes.status === 'fulfilled' && agentRes.value.ok) {
      const data = await agentRes.value.json();
      for (const c of data.conversations ?? []) {
        // Only include active/paused agent conversations (not talk-sourced ones, those are already in talkSessions)
        const ctx = c.context ?? {};
        if (ctx._source === 'talk') continue;
        agentSessions.push({
          id: c.id,
          title: c.title,
          type: 'agent',
          active_project_id: c.project_id,
          message_count: c.tool_calls_count || 0,
          created_at: c.created_at,
          updated_at: c.updated_at,
          _source: 'agent' as const,
        });
      }
    }

    // Merge and sort by updated_at desc
    const merged = [...talkSessions, ...agentSessions];
    merged.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
    return merged;
  },

  get: async (id: string): Promise<TalkSessionInfo> => {
    const res = await fetch(`/api/ask/sessions/${id}`, { credentials: 'include' });
    if (!res.ok) throw new Error(`Failed to get session: ${res.status}`);
    const data = await res.json();
    return data.session;
  },

  getMessages: async (id: string): Promise<TalkSessionMessage[]> => {
    const res = await fetch(`/api/ask/sessions/${id}/messages`, { credentials: 'include' });
    if (!res.ok) throw new Error(`Failed to get session messages: ${res.status}`);
    const data = await res.json();
    return data.messages;
  },

  archive: async (id: string): Promise<void> => {
    const res = await fetch(`/api/ask/sessions/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`Failed to archive session: ${res.status}`);
  },
};
